"use client"

import * as React from "react"
import { Link } from "react-router"
import { KeyMetrics } from "@/components/key-metrics"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { StatusBadge } from "@/components/ui/status-badge"
import { Tip } from "@/components/ui/tip"
import { TintedIconDisc, type TintedIconDiscTone } from "@/components/tinted-icon-disc"
import {
  DashboardSectionIntro,
  DashboardSectionTitle,
  dashboardSectionTitleClassName,
} from "@/components/dashboard-section-heading"
import { OperationsLifecycleSection } from "./operations/operations-lifecycle-section"
import { EVAL_DASHBOARD_KPI_STRIP_CLASS } from "@/lib/evaluation-dashboard-a11y"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  FloatingSheetPanel,
  FloatingSheetPanelBody,
  FloatingSheetPanelContent,
  FloatingSheetPanelHeader,
  FloatingSheetPanelToolbar,
  FloatingSheetPanelWorkflowFooter,
  railTriggerProps,
} from "@/lib/floating-sheet-panel"
import {
  evaluationDistributionPath,
  evaluationTermDetailsPath,
  evaluationTermSurveysPath,
  evaluationTermsPath,
} from "@/lib/evaluation-routes"
import { formatTermDateRange, termTimelineStatusMeta } from "@/lib/evaluation-term-utils"
import { setEvaluationDashboardApproach } from "@/hooks/use-evaluation-dashboard-approach"
import { useProduct } from "@/contexts/product-context"
import type { EvaluationTerm } from "@/lib/mock/evaluation-dashboard"
import { previewCourses } from "@/lib/mock/evaluation-schedule-courses"
import {
  OPERATIONS_V2_QUEUE_VISIBLE_MAX,
  buildOperationsV2OtherTerms,
  buildOperationsV2Queue,
  isLiveResponseBelowTarget,
  operationsV2LiveTermMetrics,
  operationsV2QueueCourseCount,
  type OperationsV2QueueItem,
} from "@/lib/mock/evaluation-operations-v2"

const DO_NEXT_COURSE_PREVIEW_MAX = 8

interface EvaluationDashboardOperationsV2ViewProps {
  currentTerm?: EvaluationTerm
  lastTerm?: EvaluationTerm
  upcomingTerms: EvaluationTerm[]
  futureTerms: EvaluationTerm[]
  allTerms: EvaluationTerm[]
}

export function EvaluationDashboardOperationsV2View({
  currentTerm,
  lastTerm,
  upcomingTerms,
  futureTerms,
  allTerms,
}: EvaluationDashboardOperationsV2ViewProps) {
  const { product } = useProduct()
  const queue = React.useMemo(
    () => buildOperationsV2Queue(product, currentTerm, lastTerm),
    [product, currentTerm, lastTerm],
  )
  const liveMetrics = React.useMemo(
    () => (currentTerm ? operationsV2LiveTermMetrics(product, currentTerm) : []),
    [product, currentTerm],
  )
  const otherTerms = React.useMemo(
    () =>
      buildOperationsV2OtherTerms(product, {
        lastTerm,
        upcomingTerms,
        futureTerms,
      }),
    [product, lastTerm, upcomingTerms, futureTerms],
  )
  const termsPath = evaluationTermsPath(product)
  const showLowResponse =
    currentTerm != null && isLiveResponseBelowTarget(currentTerm)

  return (
    <div className="flex flex-col gap-10">
      <DoNextSection
        items={queue}
        currentTerm={currentTerm}
        lastTerm={lastTerm}
      />

      {currentTerm ? (
        <LiveTermSection
          term={currentTerm}
          metrics={liveMetrics}
          showLowResponse={showLowResponse}
          lowResponseHref={evaluationDistributionPath(product, {
            termId: currentTerm.id,
            action: "remind",
          })}
        />
      ) : null}

      {currentTerm ? <OperationsLifecycleSection term={currentTerm} /> : null}

      <OtherTermsSection
        rows={otherTerms}
        allTermCount={allTerms.length}
        termsPath={termsPath}
      />
    </div>
  )
}

function termNameChips(termLabel: string): string[] {
  return termLabel
    .split(" and ")
    .map(name => name.trim())
    .filter(Boolean)
}

function QueueTermChips({ termLabel }: { termLabel: string }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {termNameChips(termLabel).map(termName => (
        <Badge key={termName} variant="outline">
          <i
            className="fa-light fa-calendar-days"
            data-icon="inline-start"
            aria-hidden
          />
          {termName}
        </Badge>
      ))}
    </div>
  )
}

function queueIconDiscTone(tone: OperationsV2QueueItem["tone"]): TintedIconDiscTone {
  if (tone === "danger") return "destructive"
  if (tone === "warning") return "chart-4"
  if (tone === "info") return "chart-2"
  return "brand"
}

function DoNextRow({
  item,
  primary,
  onOpen,
  onDelete,
}: {
  item: OperationsV2QueueItem
  primary: boolean
  onOpen: (item: OperationsV2QueueItem) => void
  onDelete?: (item: OperationsV2QueueItem) => void
}) {
  return (
    <li className="flex flex-wrap items-start justify-between gap-3 px-4 py-4">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Tip label={item.badgeLabel}>
          <span
            role="img"
            aria-label={item.badgeLabel}
            tabIndex={0}
            className="inline-flex size-8 shrink-0 items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <TintedIconDisc
              icon={item.icon}
              tone={queueIconDiscTone(item.tone)}
              size="md"
            />
          </span>
        </Tip>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-pretty text-foreground">{item.message}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <StatusBadge
              label={item.badgeLabel}
              tone={item.tone}
              size="md"
              icon={item.icon}
              className="whitespace-nowrap"
            />
            <QueueTermChips termLabel={item.termLabel} />
          </div>
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant={primary ? "default" : "outline"}
          {...railTriggerProps()}
          onClick={() => onOpen(item)}
        >
          {item.ctaLabel}
        </Button>
        {item.secondaryCtaLabel ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onDelete?.(item)}
          >
            {item.secondaryCtaLabel}
          </Button>
        ) : null}
      </div>
    </li>
  )
}

function DoNextSection({
  items,
  currentTerm,
  lastTerm,
}: {
  items: OperationsV2QueueItem[]
  currentTerm?: EvaluationTerm
  lastTerm?: EvaluationTerm
}) {
  const [doneIds, setDoneIds] = React.useState<string[]>([])
  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  const [shown, setShown] = React.useState<OperationsV2QueueItem | null>(null)
  const [deleteItem, setDeleteItem] = React.useState<OperationsV2QueueItem | null>(null)

  const remaining = React.useMemo(
    () => items.filter(item => !doneIds.includes(item.id)),
    [items, doneIds],
  )
  const visible = remaining.slice(0, OPERATIONS_V2_QUEUE_VISIBLE_MAX)
  const truncated = remaining.length > OPERATIONS_V2_QUEUE_VISIBLE_MAX
  const selected = remaining.find(item => item.id === selectedId) ?? null

  React.useEffect(() => {
    if (selected) setShown(selected)
  }, [selected])

  const shownIndex = shown
    ? remaining.findIndex(item => item.id === shown.id)
    : -1

  function completeItem(item: OperationsV2QueueItem) {
    setDoneIds(ids => (ids.includes(item.id) ? ids : [...ids, item.id]))
    setSelectedId(null)
    setDeleteItem(null)
  }

  const preview =
    shown != null
      ? previewCourses(shown.id, operationsV2QueueCourseCount(shown, currentTerm, lastTerm))
      : []
  const previewShown = preview.slice(0, DO_NEXT_COURSE_PREVIEW_MAX)
  const previewMore = preview.length - previewShown.length

  return (
    <>
      <section aria-labelledby="eval-ops-v2-do-next-heading" className="flex flex-col gap-4">
        <DashboardSectionIntro
          title="Do Next"
          titleId="eval-ops-v2-do-next-heading"
          actions={
            truncated ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setEvaluationDashboardApproach("action")}
              >
                View all
              </Button>
            ) : null
          }
        />

        {remaining.length === 0 ? (
          <div
            className="rounded-xl border border-border px-4 py-10 text-center"
            role="status"
          >
            <p className="text-sm font-medium text-foreground">Nothing to do next.</p>
          </div>
        ) : (
          <ul
            className="divide-y divide-border overflow-hidden rounded-xl border border-border"
            aria-labelledby="eval-ops-v2-do-next-heading"
          >
            {visible.map((item, index) => (
              <DoNextRow
                key={item.id}
                item={item}
                primary={index === 0}
                onOpen={next => setSelectedId(next.id)}
                onDelete={
                  item.secondaryCtaLabel ? next => setDeleteItem(next) : undefined
                }
              />
            ))}
          </ul>
        )}
      </section>

      <FloatingSheetPanel
        open={selected != null}
        onOpenChange={open => {
          if (!open) setSelectedId(null)
        }}
      >
        <FloatingSheetPanelContent size="md" contentSlot="surveys-do-next">
          <FloatingSheetPanelToolbar
            onPrevious={
              shownIndex > 0
                ? () => setSelectedId(remaining[shownIndex - 1]!.id)
                : undefined
            }
            onNext={
              shownIndex >= 0 && shownIndex < remaining.length - 1
                ? () => setSelectedId(remaining[shownIndex + 1]!.id)
                : undefined
            }
            previousLabel="Previous task"
            nextLabel="Next task"
          />
          <FloatingSheetPanelHeader
            title={shown?.message ?? "Do Next"}
            subtitle={shown?.termLabel}
          />
          <FloatingSheetPanelBody>
            {shown ? (
              <ul className="flex flex-col gap-3 px-4 pb-6">
                {previewShown.map(course => (
                  <li key={course.id} className="min-w-0">
                    <p className="font-mono text-sm tabular-nums text-foreground">
                      {course.courseCode}
                    </p>
                    <p className="text-sm text-pretty text-foreground">{course.title}</p>
                    <p className="text-xs text-muted-foreground">{course.facultyName}</p>
                  </li>
                ))}
                {previewMore > 0 ? (
                  <li className="text-sm text-muted-foreground">
                    {previewMore === 1 ? "1 more" : `${previewMore} more`}
                  </li>
                ) : null}
              </ul>
            ) : null}
          </FloatingSheetPanelBody>
          {shown ? (
            <FloatingSheetPanelWorkflowFooter
              onCancel={() => setSelectedId(null)}
              primaryLabel={shown.ctaLabel}
              onPrimary={() => completeItem(shown)}
              primaryIconClassName="fa-light fa-check text-xs"
            />
          ) : null}
        </FloatingSheetPanelContent>
      </FloatingSheetPanel>

      <AlertDialog
        open={deleteItem != null}
        onOpenChange={open => {
          if (!open) setDeleteItem(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete scheduled evaluations?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteItem
                ? `${operationsV2QueueCourseCount(deleteItem, currentTerm, lastTerm)} scheduled evaluations will be removed from ${deleteItem.termLabel}.`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (deleteItem) completeItem(deleteItem)
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function LiveTermSection({
  term,
  metrics,
  showLowResponse,
  lowResponseHref,
}: {
  term: EvaluationTerm
  metrics: ReturnType<typeof operationsV2LiveTermMetrics>
  showLowResponse: boolean
  lowResponseHref: string
}) {
  const { product } = useProduct()
  const timeline = termTimelineStatusMeta(term.timelinePosition)
  const dateRange = formatTermDateRange(term.startDate, term.endDate)
  const sectionId = "eval-ops-v2-live-term"

  return (
    <section aria-labelledby={`${sectionId}-heading`} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className={dashboardSectionTitleClassName} id={`${sectionId}-heading`}>
              {term.termName}
            </h2>
            <StatusBadge
              label={timeline.label}
              tone={timeline.tone}
              size="md"
              icon={timeline.icon}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            {term.programName} · {dateRange} ·{" "}
            {term.courseOfferings === 1 ? "1 course" : `${term.courseOfferings} courses`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" asChild>
            <Link to={evaluationTermDetailsPath(product, term.id)}>View details</Link>
          </Button>
          <Button size="sm" variant="outline" asChild>
            <Link to={evaluationTermSurveysPath(product, term.id)}>View surveys</Link>
          </Button>
        </div>
      </div>

      <KeyMetrics
        variant="cards"
        size="md"
        metrics={metrics}
        showHeader={false}
        metricsSingleRow
        className={EVAL_DASHBOARD_KPI_STRIP_CLASS}
      />

      {showLowResponse ? (
        <div>
          <Button size="sm" variant="outline" asChild>
            <Link to={lowResponseHref}>View low response</Link>
          </Button>
        </div>
      ) : null}
    </section>
  )
}

function OtherTermsSection({
  rows,
  allTermCount,
  termsPath,
}: {
  rows: ReturnType<typeof buildOperationsV2OtherTerms>
  allTermCount: number
  termsPath: string
}) {
  return (
    <section aria-labelledby="eval-ops-v2-other-terms-heading" className="flex flex-col gap-4">
      <DashboardSectionTitle as="h2" id="eval-ops-v2-other-terms-heading">
        Other terms
      </DashboardSectionTitle>

      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
          No other terms to show.
        </div>
      ) : (
        <ul className="divide-y divide-border rounded-xl border border-border">
          {rows.map(row => {
            const timeline = termTimelineStatusMeta(row.term.timelinePosition)
            return (
              <li
                key={row.term.id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-4"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium text-foreground">{row.term.termName}</p>
                    <StatusBadge
                      label={timeline.label}
                      tone={timeline.tone}
                      size="md"
                      icon={timeline.icon}
                    />
                    {row.term.needsAttention ? (
                      <StatusBadge
                        label="Needs attention"
                        tone="warning"
                        size="md"
                        icon="fa-triangle-exclamation"
                      />
                    ) : null}
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {row.term.programName} · {row.facts}
                  </p>
                  {row.statusNote ? (
                    <p className="mt-1 text-sm text-foreground">{row.statusNote}</p>
                  ) : null}
                </div>
                {row.actions.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {row.actions.map(action => (
                      <Button key={action.href} size="sm" variant="outline" asChild>
                        <Link to={action.href}>{action.label}</Link>
                      </Button>
                    ))}
                  </div>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}

      {allTermCount > 0 ? (
        <div>
          <Button size="sm" variant="outline" asChild>
            <Link to={termsPath}>
              {allTermCount === 1 ? "View all 1 term" : `View all ${allTermCount} terms`}
            </Link>
          </Button>
        </div>
      ) : null}
    </section>
  )
}
